import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import { FiX, FiUser, FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const LoginContainer = styled.div`
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  background: linear-gradient(135deg, #FFFAE6 0%, #FFFBE9 100%);
  color: #232b3a;
  text-align: center;
  overflow-y: auto;
  border-radius: 18px;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #e89b3c;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.2s;
  z-index: 10;
  
  &:hover {
    background: #e89b3c;
    color: white;
    transform: scale(1.1);
  }
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
  color: #e89b3c;
`;

const Subtitle = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  opacity: 0.8;
  line-height: 1.5;
  color: #6b7280;
`;

const ModeToggle = styled.div`
  display: flex;
  background: white;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 24px;
  border: 2px solid #ffd700;
  width: 100%;
  max-width: 320px;
`;

const ModeButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: ${props => props.active ? 'linear-gradient(135deg, #e89b3c 0%, #ff9900 100%)' : 'transparent'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
`;

const Form = styled.form`
  width: 100%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 4px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  color: #9ca3af;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 14px 14px 40px;
  border: 2px solid #ffd700;
  border-radius: 12px;
  font-size: 15px;
  background: white;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
`;

const Button = styled.button`
  width: 100%;
  padding: 14px 0;
  background: linear-gradient(135deg, #e89b3c 0%, #ff9900 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 12px;
  cursor: pointer;
`;

const ErrorMsg = styled.div`
  background: #fef2f2;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const SuccessMsg = styled.div`
  background: #f0fdf4;
  color: #16a34a;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const FeatureList = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
`;

const FeatureItem = styled.div`
  margin-bottom: 8px;
  font-size: 13px;
  color: #6b7280;
  
  &::before {
    content: '✓';
    color: #e89b3c;
    margin-right: 6px;
  }
`;

interface ChatLoginFormProps {
  onLoginSuccess: () => void;
  onClose?: () => void;
}

const ChatLoginForm: React.FC<ChatLoginFormProps> = ({ onLoginSuccess, onClose }) => {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const { signIn, signUp, loading, error, setError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    setError(null);

    try {
      if (mode === 'signup') {
        if (!name.trim()) {
          setFormError('Please enter your name');
          return;
        }
        await signUp(email, password, name);
        setFormSuccess(`Account created successfully!`);
      } else {
        await signIn(email, password);
        setFormSuccess('Welcome back!');
      }

      setTimeout(() => {
        onLoginSuccess();
        navigate('/dashboard');
      }, 1000);
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Authentication failed. Please try again.';
      setFormError(msg);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setFormError(null);
    setFormSuccess(null);
    setError(null);
    setName('');
    setEmail('');
    setPassword('');
  };

  return (
    <LoginContainer>
      {onClose && (
        <CloseButton onClick={onClose} title="Close">
          <FiX />
        </CloseButton>
      )}

      <Title>{mode === 'signin' ? 'Welcome Back' : 'Create Account'}</Title>
      <Subtitle>
        {mode === 'signin'
          ? 'Sign in to continue your dog  training journey'
          : 'Join us to start your dog  training journey'}
      </Subtitle>

      <ModeToggle>
        <ModeButton active={mode === 'signup'} onClick={() => setMode('signup')}>
          Sign Up
        </ModeButton>
        <ModeButton active={mode === 'signin'} onClick={() => setMode('signin')}>
          Sign In
        </ModeButton>
      </ModeToggle>

      {(formError || error) && <ErrorMsg>{formError || error}</ErrorMsg>}
      {formSuccess && <SuccessMsg>{formSuccess}</SuccessMsg>}

      <Form onSubmit={handleSubmit}>
        {mode === 'signup' && (
          <InputGroup>
            <InputWrapper>
              <InputIcon>
                <FiUser size={16} />
              </InputIcon>
              <Input
                type="text"
                placeholder="Your Full Name"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={loading}
                required
              />
            </InputWrapper>
          </InputGroup>
        )}

        <InputGroup>
          <InputWrapper>
            <InputIcon>
              <FiMail size={16} />
            </InputIcon>
            <Input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={loading}
              required
            />
          </InputWrapper>
        </InputGroup>

        <InputGroup>
          <InputWrapper>
            <InputIcon>
              <FiLock size={16} />
            </InputIcon>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={loading}
              required
            />
            <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </PasswordToggle>
          </InputWrapper>
        </InputGroup>

        <Button type="submit" disabled={loading}>
          {loading ? (
            <>
              <LoadingSpinner />
              {mode === 'signin' ? 'Signing In...' : 'Creating Account...'}
            </>
          ) : (
            mode === 'signin' ? 'Sign In' : 'Create Account'
          )}
        </Button>
      </Form>
    </LoginContainer>
  );
};

export default ChatLoginForm;
